// src/requestsAdmin/index.js
//
// v132.79 - the Admin "Customer requests" tab: any week's list of items
// customers asked for that we don't stock, grouped across stores, with the AI
// summary (Head Office, when one has been generated) and a CSV download.
// Head Office sees every store; an area manager sees their own. Read-only.
//
// Opening this tab never calls the AI: the summary shown is the cached one
// from the preview or the Monday send.
//
// globalName RequestsAdmin; every onclick target is EXPORTED (v125.14).

import { requestsCsv } from '../worker/customer-requests.js';

var _data = null;
var _week = null;      // any day in the wanted week; null = last week (the server default)

function esc(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
function root() { return document.getElementById('custRequestsRoot'); }

// The admin app has no global api(); build the call from DB (the v132.47 lesson).
async function get(path) {
  var token = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  var headers = {};
  if (token) headers.Authorization = 'Bearer ' + token;
  var res = await fetch(base + path, { method: 'GET', headers: headers, cache: 'no-store' });
  var d = await res.json().catch(function () { return {}; });
  if (!res.ok) throw new Error(d.error || ('HTTP ' + res.status));
  return d;
}

// Minimal markdown for the summary: headings, bullets, bold. Escapes first.
function mdLite(text) {
  return esc(text).split(/\r?\n/).map(function (line) {
    var l = line.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>');
    if (/^\s*#{1,6}\s+/.test(l)) return '<div style="font-weight:800;margin:10px 0 4px">' + l.replace(/^\s*#{1,6}\s+/, '') + '</div>';
    if (/^\s*[-*]\s+/.test(l)) return '<div style="padding-inline-start:14px">&bull; ' + l.replace(/^\s*[-*]\s+/, '') + '</div>';
    if (/^\s*\d+\)\s+|^[A-Z][A-Z ,\-]{3,}:?\s*$/.test(l)) return '<div style="font-weight:800;margin:10px 0 4px">' + l + '</div>';
    return l ? '<div>' + l + '</div>' : '';
  }).join('');
}

function bar() {
  return '<div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-bottom:12px">' +
    '<b style="font-size:15px">🛍 Customer requests</b>' +
    '<label for="crqWeek" style="font-size:12.5px;color:var(--sub)">Week containing</label>' +
    '<input type="date" id="crqWeek" value="' + esc(_week || (_data && _data.week_start) || '') + '" style="font-family:inherit;font-size:12.5px;padding:5px 8px;border:1px solid var(--border-strong);border-radius:8px">' +
    '<button class="btn btn-sm btn-outline" onclick="RequestsAdmin.pickWeek()">Show</button>' +
    '<button class="btn btn-sm btn-outline" onclick="RequestsAdmin.downloadCsv()">⬇ CSV</button>' +
    '</div>';
}

function paint() {
  var el = root(); if (!el) return;
  if (!_data) { el.innerHTML = bar() + '<div style="color:var(--sub);padding:16px">Loading...</div>'; return; }
  if (_data.error) { el.innerHTML = bar() + '<div style="color:var(--adm-red-text);padding:16px">' + esc(_data.error) + '</div>'; return; }
  if (_data.ready === false) { el.innerHTML = bar() + '<div style="padding:16px">The customer requests tables are not set up yet (migration v223-0).</div>'; return; }
  var d = _data;
  var summary = d.summary && d.summary.text
    ? '<div class="card" style="margin-bottom:14px"><div style="font-size:11px;color:var(--sub);margin-bottom:6px">SUMMARY (written by AI from the lines below)</div><div translate="no">' + mdLite(d.summary.text) + '</div></div>'
    : (d.scope === 'all' ? '<div style="font-size:12.5px;color:var(--sub);margin-bottom:12px">No AI summary for this week yet. It is written when the Monday email is previewed or sent (Automated emails).</div>' : '');
  var rows = (d.groups || []).map(function (g) {
    // v135.2: the item names and each store's line are what customers asked for, as typed: never translated
    return '<tr><td translate="no" style="padding:6px 8px;font-weight:700">' + esc(g.label) + '</td><td style="padding:6px 8px;text-align:end"><b>' + g.asks + '</b></td>' +
      '<td style="padding:6px 8px;text-align:end">' + g.stores + '</td><td style="padding:6px 8px">' + esc((g.countries || []).join(', ')) + '</td></tr>' +
      (g.lines || []).map(function (l) {
        return '<tr><td colspan="4" translate="no" style="padding:3px 8px 3px 26px;font-size:12.5px;color:var(--text)">' + esc(l.store) + ': "' + esc(l.item) + '"' +
          (l.brand ? ' (' + esc(l.brand) + ')' : '') + ', ' + l.asks + (l.note ? ' <span style="color:var(--sub)">: ' + esc(l.note) + '</span>' : '') + '</td></tr>';
      }).join('');
  }).join('');
  var list = function (a) { return (a && a.length) ? '<span translate="no">' + esc(a.join(', ')) + '</span>' : 'none'; };   // v135.2: store names
  el.innerHTML = bar() +
    '<div style="font-size:13px;color:var(--text);margin-bottom:10px">Week ' + esc(d.week_start) + ' to ' + esc(d.week_end) + ': <b>' + (d.totals ? d.totals.asks : 0) + '</b> customer asks, <b>' +
      (d.submitted || []).length + '</b> of ' + d.stores_total + ' stores submitted' + (d.scope === 'area' ? ' (your area)' : '') + '.</div>' +
    summary +
    (rows
      ? '<div class="card" style="overflow-x:auto"><table style="width:100%;border-collapse:collapse;font-size:13.5px"><tr>' +
          '<th align="left" style="padding:6px 8px">Item</th><th align="right" style="padding:6px 8px">Asks</th><th align="right" style="padding:6px 8px">Stores</th><th align="left" style="padding:6px 8px">Country</th></tr>' +
          rows + '</table></div>'
      : '<div class="card">No items were reported this week.</div>') +
    '<div style="font-size:12.5px;margin-top:12px;line-height:1.7">' +
      '<div><b>No requests this week:</b> ' + list(d.none_this_week) + '</div>' +
      '<div><b>Listed items but did not submit:</b> ' + list(d.not_submitted_with_lines) + '</div>' +
      '<div><b>Nothing received:</b> ' + list(d.missing) + '</div>' +
    '</div>';
}

export async function render() {
  _data = null; paint();
  try { _data = await get('/api/admin/customer-requests' + (_week ? '?week=' + encodeURIComponent(_week) : '')); }
  catch (e) { _data = { error: 'Could not load: ' + ((e && e.message) || e) }; }
  paint();
}

export function pickWeek() {
  var v = (document.getElementById('crqWeek') || {}).value || '';
  _week = /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null;
  render();
}

export function downloadCsv() {
  if (!_data || !_data.lines) return;
  var csv = requestsCsv(_data.lines, _data.week_start);
  try {
    var blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    var a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'customer-requests-' + _data.week_start + '.csv';
    document.body.appendChild(a); a.click();
    setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
  } catch (e) { if (typeof showToast === 'function') showToast('Could not create the file', 'error'); }
}
